import { createRouter, createWebHistory, RouteRecordRaw, LocationQueryRaw } from 'vue-router'
import UpcomingDrawsView from '../views/UpcomingDrawsView.vue'
import WidgetUpcomingView from '../views/WidgetUpcomingView.vue'
import LuckyNumbersView from '../views/LuckyNumbersView.vue'
import WagersView from '../views/WagersView.vue'
import ResultsView from '../views/ResultsView.vue'
import HowToPlayView from '../views/HowToPlayView.vue'
import DetailView from '../views/DetailView.vue'
import TermsAndConditionsView from '../views/TermsAndConditionsView.vue'
import { iframeCommunication } from '@/utils/iframeCommunication'
import { useAuthStore } from '@/stores/auth'
import { useGameStore } from '@/stores/game'
import { useThemeStore } from '@/stores/theme'
import { initializeTheme } from '@/composables/useTheme'
import { setLocale, SUPPORTED_LOCALES, type SupportedLocale } from '@/i18n'
import { syncSystemColorSchemeListener } from '@/utils/systemColorSchemeWatcher'
import {
  normalizeIntegrationMode,
  persistIntegrationMode,
} from '@/utils/integrationMode'
import { parseViewQuery } from '@/utils/deepLink'
import { usePromotionSync } from '@/composables/usePromotionSync'

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    redirect: (to) => ({ path: '/all', query: to.query })
  },
  {
    path: '/all',
    name: 'all',
    component: UpcomingDrawsView,
    meta: { activeNav: 'all' }
  },
  {
    path: '/widget/upcoming',
    name: 'widget-upcoming',
    component: WidgetUpcomingView,
    meta: { activeNav: 'all' }
  },
  {
    path: '/lottery/:slug',
    name: 'lottery-detail',
    component: DetailView,
    meta: { activeNav: 'all' }
  },
  {
    path: '/next-draws',
    redirect: (to) => ({ path: '/all', query: to.query })
  },
  {
    path: '/lucky-numbers',
    name: 'lucky-numbers',
    component: LuckyNumbersView,
    meta: { activeNav: 'lucky-numbers' }
  },
  {
    path: '/wagers',
    name: 'wagers',
    component: WagersView,
    meta: { activeNav: 'wagers', requiresAuth: true }
  },
  {
    path: '/results',
    name: 'results',
    component: ResultsView,
    meta: { activeNav: 'results' }
  },
  {
    path: '/how-to-play',
    name: 'how-to-play',
    component: HowToPlayView,
    meta: { activeNav: 'how-to-play' }
  },
  {
    path: '/terms-and-conditions',
    name: 'terms-and-conditions',
    component: TermsAndConditionsView,
    meta: { activeNav: 'terms-and-conditions' }
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior() {
    return { top: 0, behavior: 'smooth' }
  }
})

router.beforeEach((to, from, next) => {
  /** Only normalize `locale` in the URL; `mode` is optional and defaults in main.ts / afterEach without rewriting the query. */
  const defaultParams = {
    locale: 'en'
  }

  const mergedQuery: LocationQueryRaw = {
    ...defaultParams,
    ...from.query,
    ...to.query
  }

  // Only `integration=lite` enables reduced UI; `minimal` / `full` / omitted → full app.
  if (to.query.integration !== undefined) {
    const integrationFromTo = normalizeIntegrationMode(to.query.integration)
    if (integrationFromTo === 'lite') {
      persistIntegrationMode('lite')
      mergedQuery.integration = 'lite'
    } else {
      persistIntegrationMode(null)
      delete mergedQuery.integration
    }
  } else {
    persistIntegrationMode(null)
    delete mergedQuery.integration
  }

  const gameRaw = to.query.game ?? to.query.gameSlug
  const gameSlug = Array.isArray(gameRaw)
    ? gameRaw[0]?.trim()
    : typeof gameRaw === 'string'
      ? gameRaw.trim()
      : ''
  if (gameSlug && to.name !== 'lottery-detail') {
    const gameQuery = { ...mergedQuery }
    delete gameQuery.game
    delete gameQuery.gameSlug
    next({ path: `/lottery/${gameSlug}`, query: gameQuery })
    return
  }

  const initialView = parseViewQuery(to.query.view)
  if (initialView === 'lucky-numbers' && to.name !== 'lucky-numbers' && to.name !== 'widget-upcoming') {
    const viewQuery = { ...mergedQuery }
    delete viewQuery.view
    next({ name: 'lucky-numbers', query: viewQuery })
    return
  }
  if (
    initialView === 'all'
    && to.name !== 'all'
    && to.name !== 'lottery-detail'
    && to.name !== 'widget-upcoming'
  ) {
    const viewQuery = { ...mergedQuery }
    delete viewQuery.view
    next({ name: 'all', query: viewQuery })
    return
  }

  const shouldPreserveTheme = !!from.query.theme && !to.query.theme

  const needsRedirect = !to.query.locale || shouldPreserveTheme

  const authStore = useAuthStore()
  
  // URL query params take precedence, but preserve session for internal navigation
  const hasUrlToken = !!to.query.token
  const hasUrlAccount = !!to.query.account
  const hasSessionToken = sessionStorage.getItem('auth_token')
  const hasSessionAccount = sessionStorage.getItem('auth_account')
  
  if (hasUrlToken) {
    // URL has token - use it and update session
    const cleanToken = (to.query.token as string).split('?')[0]
    sessionStorage.setItem('auth_token', cleanToken)
    authStore.token = cleanToken
  } else if (hasSessionToken && !authStore.token) {
    // No URL token but session exists - restore from session
    authStore.token = hasSessionToken
  }
  // Note: We no longer clear auth on internal navigation
  
  if (hasUrlAccount) {
    // URL has account - use it and update session
    const cleanAccount = (to.query.account as string).split('?')[0]
    sessionStorage.setItem('auth_account', cleanAccount)
    authStore.account = cleanAccount
  } else if (hasSessionAccount && !authStore.account) {
    // No URL account but session exists - restore from session
    authStore.account = hasSessionAccount
  }
  // Note: We no longer clear auth on internal navigation
  
  if (to.query.currency) {
    const cleanCurrency = (to.query.currency as string).split('?')[0]
    sessionStorage.setItem('auth_currency', cleanCurrency)
  }

  if (to.meta.requiresAuth) {
    const hasSessionToken = !!sessionStorage.getItem('auth_token')
    const hasSessionAccount = !!sessionStorage.getItem('auth_account')
    const hasUrlToken = !!to.query.token
    const hasUrlAccount = !!to.query.account
    
    if (!hasUrlToken && !hasUrlAccount && !hasSessionToken && !hasSessionAccount) {
      console.warn('Authentication parameters (token, account) are required for this route')
    }
  }

  if (needsRedirect) {
    next({ ...to, query: mergedQuery })
  } else {
    next()
  }
})

router.afterEach(async (to, from) => {
  const modeParam =
    typeof to.query.mode === 'string'
      ? to.query.mode
      : Array.isArray(to.query.mode) && typeof to.query.mode[0] === 'string'
        ? to.query.mode[0]
        : undefined
  const themeStore = useThemeStore()
  const normalizedMode = modeParam?.trim().toLowerCase()
  if (!themeStore.lockedColorMode) {
    if (normalizedMode === 'system' || normalizedMode === 'auto') {
      themeStore.clearUserColorModeOverride()
      themeStore.enableSystemColorScheme()
      syncSystemColorSchemeListener()
    } else if (normalizedMode === 'light' || normalizedMode === 'dark') {
      if (themeStore.followSystemColorScheme || themeStore.mode !== normalizedMode) {
        themeStore.setMode(normalizedMode)
      }
      syncSystemColorSchemeListener()
    } else {
      syncSystemColorSchemeListener()
    }
  } else {
    syncSystemColorSchemeListener()
  }

  // Only re-initialize theme if account or theme query param changed
  const accountChanged = to.query.account !== from.query.account
  const themeChanged = to.query.theme !== from.query.theme
  
  if (accountChanged || themeChanged) {
    try {
      await initializeTheme(true)
    } catch (error) {
      console.error('Failed to re-initialize theme:', error)
    }
  }

  // Sync locale with i18n when locale query param changes
  const localeParam = (to.query.locale as string) || (to.query.lang as string)
  if (localeParam && SUPPORTED_LOCALES.includes(localeParam as SupportedLocale)) {
    const localeChanged = localeParam !== from.query.locale && localeParam !== from.query.lang
    if (localeChanged || !from.name) {
      try {
        await setLocale(localeParam as SupportedLocale)
      } catch (error) {
        console.error('Failed to set locale:', error)
      }
    }
  }
  
  if (iframeCommunication.isRunningInIframe()) {
    iframeCommunication.sendRouteChange(to.path, to.meta.title as string || document.title)
  }

  const bettingRouteNames = new Set(['all', 'lottery-detail', 'lucky-numbers'])
  if (bettingRouteNames.has(String(to.name))) {
    const authStore = useAuthStore()
    authStore.refreshAuthFromExternalSources()
    const account =
      authStore.account ||
      (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('auth_account') : null)
    const rawCur = to.query.currency
    const fromQuery =
      typeof rawCur === 'string'
        ? rawCur.split('?')[0]
        : Array.isArray(rawCur) && typeof rawCur[0] === 'string'
          ? rawCur[0].split('?')[0]
          : null
    const currency =
      (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('auth_currency') : null) ||
      fromQuery
    if (account && currency) {
      const gameStore = useGameStore()
      try {
        await gameStore.fetchGameSettings(false)
        const { refreshPromotions } = usePromotionSync()
        await refreshPromotions(undefined, { silent: true })
      } catch {
        /* non-fatal: game-settings optional until slip opens */
      }
    }
  }
})

export default router
